import { useAnalytics } from '@repo/analytics/analytics-provider';
import { Button } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { ContextMenu, ContextMenuItem } from '@repo/design-system/components/ui/context-menu';
import { toast } from '@repo/design-system/components/ui/toaster';
import { Type } from '@repo/design-system/components/ui/type';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import { useBulkUpdatePinStatusesMutation } from '@repo/store/maps/api/mapsApi';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { selectAuthState } from '@repo/store/maps/slices/auth';
import { selectIsMobileViewport } from '@repo/store/maps/slices/clientContext';
import {
    selectExternalMapId,
    selectIsCategoryHidden,
    selectIsInCreatorMode,
    selectMapData,
    setSelectedMarkerIds,
    showOnlyCategoryPins,
    showOnlySubcategoryPins,
    toggleCategoryFilter,
    toggleSubcategoryFilter,
} from '@repo/store/maps/slices/mapData';
import type { GroupedCategory } from '@repo/types/maps/map-types';
import { getFontInfoForGlyph, getIconForGlyph, getSubcategoryIconStyle } from '@repo/types/maps/map-utils';
import React, { useMemo, useState } from 'react';
import { MapsAuthModal } from '../components/maps-auth-modal';

const subcategoriesStyles = css({
    display: 'grid',
    gap: '4',
    width: 'full',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(120px, 100%), 1fr))',
    _compact: {
        gridTemplateColumns: '1fr',
    },
});

const subcategoryIconStyles = css({
    fontSize: 'md',
    color: 'whiteAlpha.70',
    width: '[16px]',
    textAlign: 'center',
});

export interface MapCategorySectionProps {
    category: GroupedCategory;
    searchTerm: string;
}

export const MapCategorySection: React.FC<MapCategorySectionProps> = React.memo(
    ({ category, searchTerm }: MapCategorySectionProps) => {
        const dispatch = useAppDispatch();
        const [isExpanded, setIsExpanded] = useState(true);
        const externalMapId = useAppSelector(selectExternalMapId);
        const isMobileViewport = useAppSelector(selectIsMobileViewport);
        const hostType = useHostType();
        const analytics = useAnalytics();
        const { t } = useT(['game_map']);
        const isInCreatorMode = useAppSelector(selectIsInCreatorMode);

        // Creator-only: hand a whole subcategory or category to the bulk edit tools in one click.
        const selectPinsBySubcategory = (subcategoryExternalIds: string[]) => {
            const wanted = new Set(subcategoryExternalIds);
            const ids = (mapData?.geojson.features ?? [])
                .filter(
                    feature =>
                        !feature.properties.isCustomPin && wanted.has(feature.properties.subcategoryExternalId ?? ''),
                )
                .map(feature => feature.properties.externalId);

            dispatch(setSelectedMarkerIds(ids));
            toast({
                title: `${ids.length} pin${ids.length === 1 ? '' : 's'} selected`,
                description: 'Open Bulk edit pins in Creator Tools to change them together.',
                variant: ids.length > 0 ? 'success' : 'info',
            });
        };
        const mapData = useAppSelector(selectMapData);
        const auth = useAppSelector(selectAuthState);
        const [bulkUpdatePinStatuses] = useBulkUpdatePinStatusesMutation();
        const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

        const isCategoryHidden = useAppSelector(selectIsCategoryHidden(category.externalId));
        const hiddenSubcategories = useAppSelector(state => state.mapData.filters.hiddenSubcategories);

        const subcategoryHiddenStates = useMemo(() => {
            const hiddenStates: { [key: string]: boolean } = {};
            category.subcategories.forEach(sub => {
                hiddenStates[sub.externalId] = hiddenSubcategories.includes(sub.externalId);
            });

            return hiddenStates;
        }, [category.subcategories, hiddenSubcategories]);

        const filteredSubcategories = category.subcategories.filter(sub =>
            sub.title.toLowerCase().includes(searchTerm.toLowerCase()),
        );

        if (filteredSubcategories.length === 0 && searchTerm) {
            return null;
        }

        const checkboxColor = category.original.color;

        const handleCategoryToggle = (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();

            dispatch(toggleCategoryFilter(category.externalId));
            if (analytics && externalMapId && hostType) {
                analytics.event('game_map_toggle_category', {
                    mapId: externalMapId,
                    categoryId: category.externalId,
                    filtered: !isCategoryHidden,
                    platform: hostType,
                });
            }
        };

        const handleShowOnlyCategory = () => {
            dispatch(showOnlyCategoryPins(category.externalId));
            if (analytics && externalMapId && hostType) {
                analytics.event('game_map_toggle_category', {
                    mapId: externalMapId,
                    categoryId: category.externalId,
                    filtered: true,
                    platform: hostType,
                    source: 'sidebar-context-menu',
                });
            }
        };

        const handleShowOnlySubcategory = (subcategoryId: string) => {
            dispatch(showOnlySubcategoryPins(subcategoryId));

            if (analytics && externalMapId && hostType) {
                analytics.event('game_map_toggle_subcategory', {
                    mapId: externalMapId,
                    subcategoryId,
                    filtered: true,
                    platform: hostType,
                    source: 'sidebar-context-menu',
                });
            }
        };

        const handleSubcategoryToggle = (subcategoryId: string) => {
            dispatch(toggleSubcategoryFilter(subcategoryId));

            if (analytics && externalMapId && hostType) {
                const isCurrentlyHidden = hiddenSubcategories.includes(subcategoryId);
                analytics.event('game_map_toggle_subcategory', {
                    mapId: externalMapId,
                    subcategoryId,
                    filtered: !isCurrentlyHidden,
                    platform: hostType,
                });
            }
        };

        const handleCategoryCollapse = () => {
            setIsExpanded(!isExpanded);

            if (analytics && externalMapId && hostType) {
                analytics.event('game_map_toggle_category_collapse', {
                    mapId: externalMapId,
                    categoryId: category.externalId,
                    collapsed: !isExpanded,
                    platform: hostType,
                });
            }
        };

        const getPinsForSubcategories = (subcategoryIds: string[]) => {
            if (!mapData) return [];
            const subcategorySet = new Set(subcategoryIds);
            return mapData.geojson.features
                .filter(f => subcategorySet.has(f.properties.subcategoryExternalId))
                .map(f => ({
                    pinnableType: (f.properties.isCustomPin ? 'user_pin' : 'pin') as 'pin' | 'user_pin',
                    pinnableId: f.properties.externalId,
                }));
        };

        const handleBulkUpdate = async (found: boolean, subcategoryIds: string[]) => {
            if (!externalMapId) return;
            if (!auth.isAuthenticated) {
                setIsLoginModalOpen(true);
                return;
            }

            const pins = getPinsForSubcategories(subcategoryIds);
            if (pins.length === 0) return;

            try {
                await bulkUpdatePinStatuses({ externalId: externalMapId, pins, found }).unwrap();

                if (analytics && hostType) {
                    analytics.event('game_map_bulk_update_pin_status', {
                        mapId: externalMapId,
                        found,
                        pinCount: pins.length,
                        trigger: 'sidebar-context-menu',
                        platform: hostType,
                    });
                }
            } catch {
                toast({
                    title: t($ => $.error),
                    description: found ? t($ => $.mark_all_found_error) : t($ => $.mark_all_unfound_error),
                    variant: 'error',
                });
            }
        };

        const allSubcategoryIds = category.subcategories.map(sub => sub.externalId);

        return (
            <>
                <FlexColumn
                    width="full"
                    className={css.raw({
                        contentVisibility: 'auto',
                        containIntrinsicSize: '[auto 80px]',
                    })}
                >
                    <ContextMenu
                        triggerClassName={css({ width: 'full' })}
                        trigger={
                            <FlexRow width="full" align="center" gap="12">
                                <Button
                                    variant="ghost"
                                    onClick={handleCategoryCollapse}
                                    color="primary"
                                    size="sm"
                                    radius="rounded"
                                    className={css({
                                        justifyContent: 'start',
                                        flexGrow: 1,
                                        _active: { transform: 'scale(0.97)', opacity: '0.8' },
                                    })}
                                >
                                    <Icon
                                        icon={isExpanded ? 'keyboard_arrow_down' : 'keyboard_arrow_right'}
                                        decorative
                                        className={css({ marginLeft: '-4' })}
                                    />
                                    <Type variant="caption" style={{ opacity: isCategoryHidden ? 0.5 : 1 }}>
                                        {category.title}
                                    </Type>
                                </Button>

                                <Button
                                    variant="ghost"
                                    onClick={handleCategoryToggle}
                                    color="primary"
                                    size="sm"
                                    radius="rounded"
                                    title={isCategoryHidden ? 'Show category' : 'Hide category'}
                                    className={css({
                                        minWidth: '[36px]',
                                        minHeight: '[36px]',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0',
                                        _active: { transform: 'scale(0.97)', opacity: '0.8' },
                                    })}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: '4px',
                                            minWidth: '16px',
                                            minHeight: '16px',
                                            width: '16px',
                                            height: '16px',
                                            backgroundColor: checkboxColor,
                                            position: 'relative',
                                        }}
                                        className={css({
                                            '&:focus-within': {
                                                filter: '[brightness(0.8)]',
                                            },
                                            borderColor: 'whiteAlpha.30',
                                            borderWidth: '1px',
                                            borderStyle: 'solid',
                                            borderRadius: '4',
                                        })}
                                    >
                                        {!isCategoryHidden && (
                                            <Icon
                                                className={css({ position: 'absolute' })}
                                                fill
                                                icon="check"
                                                size="md"
                                                decorative
                                            />
                                        )}
                                    </div>
                                </Button>
                            </FlexRow>
                        }
                    >
                        <ContextMenuItem
                            value="mark-found"
                            leadingIcon="check_circle"
                            onSelect={() => handleBulkUpdate(true, allSubcategoryIds)}
                        >
                            {t($ => $.mark_all_found)}
                        </ContextMenuItem>
                        <ContextMenuItem
                            value="mark-unfound"
                            leadingIcon="cancel"
                            onSelect={() => handleBulkUpdate(false, allSubcategoryIds)}
                        >
                            {t($ => $.mark_all_unfound)}
                        </ContextMenuItem>
                        {isInCreatorMode && (
                            <ContextMenuItem
                                value="select-category-pins"
                                leadingIcon="check_box"
                                onSelect={() => selectPinsBySubcategory(allSubcategoryIds)}
                            >
                                Select all pins in this category
                            </ContextMenuItem>
                        )}
                        <ContextMenuItem
                            value="show-only-category"
                            leadingIcon="filter_alt"
                            onSelect={handleShowOnlyCategory}
                        >
                            {t($ => $.show_only_category_on_map, { categoryName: category.title })}
                        </ContextMenuItem>
                        <ContextMenuItem
                            value="toggle-visibility"
                            leadingIcon={isCategoryHidden ? 'visibility' : 'visibility_off'}
                            onSelect={() => {
                                dispatch(toggleCategoryFilter(category.externalId));
                                if (analytics && externalMapId && hostType) {
                                    analytics.event('game_map_toggle_category', {
                                        mapId: externalMapId,
                                        categoryId: category.externalId,
                                        filtered: !isCategoryHidden,
                                        platform: hostType,
                                    });
                                }
                            }}
                        >
                            {isCategoryHidden
                                ? t($ => $.show_category_on_map, { categoryName: category.title })
                                : t($ => $.hide_category_on_map, { categoryName: category.title })}
                        </ContextMenuItem>
                    </ContextMenu>

                    {isExpanded && (
                        <div className={subcategoriesStyles}>
                            {filteredSubcategories.map(subcategory => {
                                const isSubcategoryHidden = subcategoryHiddenStates[subcategory.externalId] ?? false;

                                return (
                                    <ContextMenu
                                        key={subcategory.externalId}
                                        trigger={
                                            <Button
                                                variant="ghost"
                                                onClick={() => handleSubcategoryToggle(subcategory.externalId)}
                                                color="primary"
                                                size="sm"
                                                radius="rounded"
                                                fullWidth
                                                title={subcategory.title}
                                                className={css({
                                                    opacity: isSubcategoryHidden ? '0.35' : '1',
                                                    _active: { transform: 'scale(0.97)', opacity: '0.8' },
                                                    ...(isMobileViewport ? { minHeight: '[44px]' } : {}),
                                                })}
                                            >
                                                <span
                                                    className={subcategoryIconStyles}
                                                    style={getSubcategoryIconStyle(
                                                        getFontInfoForGlyph(subcategory.icon).fontName,
                                                    )}
                                                >
                                                    {getIconForGlyph(subcategory.icon)}
                                                </span>
                                                <Type color="whiteAlpha.80" variant="body-xs" truncate>
                                                    {subcategory.title}
                                                </Type>
                                                <Type
                                                    className={css({ marginLeft: 'auto' })}
                                                    color="whiteAlpha.80"
                                                    variant="caption"
                                                >
                                                    {subcategory.locationCount}
                                                </Type>
                                            </Button>
                                        }
                                    >
                                        <ContextMenuItem
                                            value="mark-found"
                                            leadingIcon="check_circle"
                                            onSelect={() => handleBulkUpdate(true, [subcategory.externalId])}
                                        >
                                            {t($ => $.mark_all_found)}
                                        </ContextMenuItem>
                                        <ContextMenuItem
                                            value="mark-unfound"
                                            leadingIcon="cancel"
                                            onSelect={() => handleBulkUpdate(false, [subcategory.externalId])}
                                        >
                                            {t($ => $.mark_all_unfound)}
                                        </ContextMenuItem>
                                        {isInCreatorMode && (
                                            <ContextMenuItem
                                                value="select-subcategory-pins"
                                                leadingIcon="check_box"
                                                onSelect={() => selectPinsBySubcategory([subcategory.externalId])}
                                            >
                                                Select all pins in this subcategory
                                            </ContextMenuItem>
                                        )}
                                        <ContextMenuItem
                                            value="show-only-subcategory"
                                            leadingIcon="filter_alt"
                                            onSelect={() => handleShowOnlySubcategory(subcategory.externalId)}
                                        >
                                            {t($ => $.show_only_category_on_map, { categoryName: subcategory.title })}
                                        </ContextMenuItem>
                                        <ContextMenuItem
                                            value="toggle-visibility"
                                            leadingIcon={isSubcategoryHidden ? 'visibility' : 'visibility_off'}
                                            onSelect={() => handleSubcategoryToggle(subcategory.externalId)}
                                        >
                                            {isSubcategoryHidden
                                                ? t($ => $.show_category_on_map, { categoryName: subcategory.title })
                                                : t($ => $.hide_category_on_map, { categoryName: subcategory.title })}
                                        </ContextMenuItem>
                                    </ContextMenu>
                                );
                            })}
                        </div>
                    )}
                </FlexColumn>
                <MapsAuthModal
                    isOpen={isLoginModalOpen}
                    onOpenChange={details => setIsLoginModalOpen(details.open)}
                    trigger="mark-found"
                    size="sm"
                />
            </>
        );
    },
);

MapCategorySection.displayName = 'MapCategorySection';
